<script setup lang="ts">
import type { MigrationStep } from '#layers/data-model/utils/migration-step-groups';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DisplayFieldPathPart from '#layers/data-model/components/Display/FieldPathPart.vue';
import {
	getMigrationStepPathParts,
	MIGRATION_STEP_TONE_DOT_COLORS,
	splitMigrationStepPath,
} from '#layers/data-model/utils/migration-step-display';
import { getMigrationStepTone } from '#layers/data-model/utils/migration-step-groups';

const props = defineProps<{
	step: MigrationStep;
}>();

const { t } = useI18n();

const pathParts = computed(() => getMigrationStepPathParts(props.step));
const nextPathParts = computed(() => getNextPathParts(props.step));
const toneDotColor = computed(() => MIGRATION_STEP_TONE_DOT_COLORS[getMigrationStepTone(props.step.kind)]);
const hasTargetPath = computed(() => nextPathParts.value.length > 0);
const valueChange = computed(() => getValueChange(props.step));
const hasValueChange = computed(() => hasValue(valueChange.value?.fromValue) && hasValue(valueChange.value?.toValue));
const fromValueLabel = computed(() => getValueLabel(valueChange.value?.fromValue, valueChange.value?.fromValueKey));
const toValueLabel = computed(() => getValueLabel(valueChange.value?.toValue, valueChange.value?.toValueKey));
const sentenceKey = computed(() => {
	if (props.step.kind === 'changeCollectionReadonly' && hasValueChange.value) {
		return 'pages.data_model.introspection.matrix.sentences.changeCollectionReadonlyWithValues';
	}

	if (props.step.kind === 'createRelation' && hasTargetPath.value) {
		return 'pages.data_model.introspection.matrix.sentences.createRelationWithTarget';
	}

	return `pages.data_model.introspection.matrix.sentences.${props.step.kind}`;
});

function getNextPathParts(step: MigrationStep) {
	const target = getTargetPath(step);

	if (!target) {
		return [];
	}

	const currentPath = getMigrationStepPathParts(step);
	const nextPath = splitMigrationStepPath(target);

	if (currentPath.length > 1 && nextPath.length === 1) {
		return [...currentPath.slice(0, -1), ...nextPath];
	}

	return nextPath;
}

function getTargetPath(step: MigrationStep) {
	return 'to' in step.params ? step.params.to : undefined;
}

function getValueChange(step: MigrationStep) {
	return 'fromValue' in step.params ? step.params : undefined;
}

function getValueLabel(value?: string, key?: string) {
	if (!hasValue(value)) {
		return;
	}

	return key ? t(key) : value;
}

function hasValue(value?: string): value is string {
	return value !== undefined;
}
</script>

<template>
	<li
		class="flex items-start gap-3 py-1.5"
	>
		<div class="mt-1.5 flex h-2 shrink-0 items-center">
			<DColorDot :color="toneDotColor" size="xs" :ui="{ base: 'h-2' }" />
		</div>

		<div class="min-w-0 flex-1 text-xs text-default">
			<span class="min-w-0">
				<i18n-t
					:keypath="sentenceKey"
					tag="span"
					class="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1"
				>
					<template #subject>
						<DFieldGroup v-if="pathParts.length > 0" class="max-w-full">
							<DisplayFieldPathPart
								v-for="(part, index) in pathParts"
								:key="`${part}-${index}`"
								size="sm"
								:label="part"
								tabindex="-1"
							/>
						</DFieldGroup>
					</template>

					<template #target>
						<DFieldGroup v-if="hasTargetPath" class="max-w-full">
							<DisplayFieldPathPart
								v-for="(part, index) in nextPathParts"
								:key="`${part}-${index}`"
								color="primary"
								size="sm"
								:label="part"
								tabindex="-1"
							/>
						</DFieldGroup>
					</template>

					<template #fromValue>
						<code
							v-if="hasValueChange"
							class="min-w-0 truncate rounded-sm border border-muted bg-muted px-1.5 py-0.5 font-mono text-xs text-default"
						>
							{{ fromValueLabel }}
						</code>
					</template>

					<template #toValue>
						<code
							v-if="hasValueChange"
							class="
								min-w-0 truncate rounded-sm border border-primary/30 bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary
							"
						>
							{{ toValueLabel }}
						</code>
					</template>
				</i18n-t>
			</span>

			<span v-if="pathParts.length === 0 && !hasTargetPath && !hasValueChange" class="sr-only">
				{{ $t('pages.data_model.introspection.matrix.no_detail') }}
			</span>
		</div>
	</li>
</template>
